import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Check, Star } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const [color, setColor] = useState(product.colors[0]);
  const [size, setSize] = useState(null);
  const needsSize = product.sizes && product.sizes.length > 0;

  const add = () => {
    if (needsSize && !size) {
      toast.error("Please select a size first.");
      return;
    }
    addItem({
      product_id: product.id,
      name: product.name,
      price: product.price,
      image: color.image,
      color: color.name,
      size: size,
    });
    toast.success(`${product.name} added to cart`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.07 }}
      className="group flex flex-col rounded-2xl overflow-hidden bg-charcoal border border-white/10 hover:border-white/20 transition-colors"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-square overflow-hidden bg-black">
        <img
          key={color.image}
          src={color.image}
          alt={`${product.name} - ${color.name}`}
          className="w-full h-full object-cover animate-[fadeIn_0.4s_ease]"
          data-testid={`product-image-${product.id}`}
        />
        <span className="absolute top-3 left-3 glass rounded-full px-3 py-1 overline text-white/70">
          {product.tagline}
        </span>
        <span className="absolute top-3 right-3 rounded-full bg-blaze/90 px-3 py-1 overline text-white">
          {product.design}
        </span>
        {product.badge && (
          <span
            className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full bg-white text-black px-3 py-1 text-[11px] font-bold uppercase tracking-wide"
            data-testid={`product-badge-${product.id}`}
          >
            <Star size={12} className="fill-blaze text-blaze" /> {product.badge}
          </span>
        )}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-heading font-700 text-base">{product.name}</h3>
          <span className="font-mono text-blaze font-bold whitespace-nowrap" data-testid={`product-price-${product.id}`}>
            ${product.price.toFixed(2)}
          </span>
        </div>

        {product.quote ? (
          <p className="mt-1 text-sm text-white/70 italic">&ldquo;{product.quote}&rdquo;</p>
        ) : (
          <p className="mt-1 text-sm text-white/40">Signature key emblem</p>
        )}

        <p className="mt-2 text-xs text-white/50 leading-relaxed line-clamp-2">{product.description}</p>

        {/* Color swatches */}
        <div className="mt-4 flex items-center gap-2">
          {product.colors.map((c) => (
            <button
              key={c.name}
              onClick={() => setColor(c)}
              title={c.name}
              aria-label={c.name}
              className={`h-6 w-6 rounded-full border transition-transform hover:scale-110 ${
                color.name === c.name ? "border-blaze ring-2 ring-blaze/40" : "border-white/25"
              }`}
              style={{ backgroundColor: c.hex }}
              data-testid={`color-${product.id}-${c.name.toLowerCase()}`}
            />
          ))}
          <span className="ml-1 text-xs text-white/50">{color.name}</span>
        </div>

        {/* Sizes */}
        {needsSize && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {product.sizes.map((s) => (
              <button
                key={s}
                onClick={() => setSize(s)}
                className={`h-8 min-w-8 px-2 rounded-md text-xs font-semibold transition-colors ${
                  size === s ? "bg-blaze text-white" : "border border-white/20 text-white/60 hover:bg-white/10"
                }`}
                data-testid={`size-${product.id}-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <button
          onClick={add}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-blaze px-4 py-3 text-sm font-bold text-white transition-transform hover:scale-[1.03] hover:blaze-glow"
          data-testid={`add-to-cart-${product.id}`}
        >
          {needsSize && size ? <Check size={16} /> : <Plus size={16} />} Add to cart
        </button>
      </div>
    </motion.div>
  );
};
